import React, { useState, useEffect, useRef } from 'react';
import { Button, TextField, Divider } from '@mui/material';
import MicIcon from "@mui/icons-material/Mic";
import MicOffIcon from "@mui/icons-material/MicOff";

const DynamicCategorySection = ({
  category,
  questions = [],
  sessionId,
  answers = [],
  onAnswerChange,
  onSave,
  CustomComponent,
  customComponentProps = {},
}) => {
  const [isListening, setIsListening] = useState({});
  const [activeQuestionId, setActiveQuestionId] = useState(null);
  const micRef = useRef(null);

  console.log(category,"missing info check");

  useEffect(() => {
    // Initialize speech recognition
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (SpeechRecognition) {
      micRef.current = new SpeechRecognition();
      micRef.current.continuous = true;
      micRef.current.interimResults = true;
      micRef.current.lang = "en-US";
    }

    return () => {
      if (micRef.current) {
        micRef.current.stop();
        micRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!micRef.current) return;

    const handleMicStart = () => {
      console.log("Mic started for question:", activeQuestionId);
    };

    const handleMicEnd = () => {
      console.log("Mic ended for question:", activeQuestionId);
      // Restart if still listening
      if (activeQuestionId && isListening[activeQuestionId]) {
        setTimeout(() => {
          if (micRef.current && isListening[activeQuestionId]) {
            micRef.current.start();
          }
        }, 100);
      }
    };

    const handleMicResult = (event) => {
      let finalTranscript = '';
      let interimTranscript = '';

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          finalTranscript += transcript;
        } else {
          interimTranscript += transcript;
        }
      }

      if (finalTranscript) {
        console.log("Final transcript:", finalTranscript);
        // Update the specific question that has the active mic
        if (activeQuestionId) {
          onAnswerChange(sessionId, activeQuestionId, null, finalTranscript);
        }
      }
    };

    const handleMicError = (event) => {
      console.log("Mic error:", event.error);
      if (event.error === 'no-speech') {
        // Restart mic if no speech detected
        if (activeQuestionId && isListening[activeQuestionId]) {
          setTimeout(() => {
            if (micRef.current && isListening[activeQuestionId]) {
              micRef.current.start();
            }
          }, 100);
        }
      }
    };

    micRef.current.onstart = handleMicStart;
    micRef.current.onend = handleMicEnd;
    micRef.current.onresult = handleMicResult;
    micRef.current.onerror = handleMicError;

    return () => {
      if (micRef.current) {
        micRef.current.onstart = null;
        micRef.current.onend = null;
        micRef.current.onresult = null;
        micRef.current.onerror = null;
      }
    };
  }, [isListening, activeQuestionId, sessionId, onAnswerChange]);

  const toggleListening = (questionId) => {
    if (!micRef.current) {
      console.log("Speech recognition not supported");
      return;
    }

    if (isListening[questionId]) {
      // Stop listening for this question
      console.log("Stopping mic for question:", questionId);
      setIsListening(prev => ({ ...prev, [questionId]: false }));
      setActiveQuestionId(null);
      micRef.current.stop();
    } else {
      // Stop any other active mic first
      if (activeQuestionId && isListening[activeQuestionId]) {
        console.log("Stopping previous mic for question:", activeQuestionId);
        setIsListening(prev => ({ ...prev, [activeQuestionId]: false }));
        micRef.current.stop();
      }
      
      // Start listening for this question
      console.log("Starting mic for question:", questionId);
      setIsListening(prev => ({ ...prev, [questionId]: true }));
      setActiveQuestionId(questionId);
      
      // Small delay to ensure previous mic is stopped
      setTimeout(() => {
        if (micRef.current) {
          micRef.current.start();
        }
      }, 200);
    }
  };

  return (
    <div style={{ margin: '20px 0' }}>
      <h2>{category.title}</h2>
      {questions.map((question, index) => {
        const existing = answers.find((a) => a.question === question.id);
        return (
          <div key={question.id}>
            <h4>{question.title}</h4>
            {question.question_type === 1 ? (
              question.choices.map((choice) => {
                const isSelected = existing?.choice === choice.id;
                return (
                  <div
                    key={choice.id}
                    onClick={() =>
                      onAnswerChange(sessionId, question.id, choice.id, null)
                    }
                    style={{
                      padding: '10px 14px',
                      border: '1px solid #999',
                      borderRadius: '6px',
                      marginBottom: '8px',
                      backgroundColor: isSelected ? '#cceeff' : '#fff',
                      fontWeight: isSelected ? 'bold' : 'normal',
                      cursor: 'pointer',
                    }}
                  >
                    {choice.title}
                  </div>
                );
              })
            ) : question.question_type === 2 ? (
              <div>
                <div style={{ display: "flex", alignItems: "center", marginBottom: 8 }}>
                  <span style={{ fontWeight: 500, marginRight: 8 }}>Voice Input:</span>
                  {isListening[question.id] ? (
                    <MicIcon 
                      sx={{ color: "#02f733", cursor: "pointer" }} 
                      onClick={() => toggleListening(question.id)} 
                      title="Click to stop voice input"
                    />
                  ) : (
                    <MicOffIcon 
                      sx={{ color: "#f71302", cursor: "pointer" }} 
                      onClick={() => toggleListening(question.id)} 
                      title="Click to start voice input"
                    />
                  )}
                  {isListening[question.id] && (
                    <span style={{ marginLeft: 8, color: "#02f733", fontSize: "12px" }}>
                      Listening...
                    </span>
                  )}
                </div>
                <TextField
                  size="small"
                  name="answer"
                  multiline
                  rows={2}
                  fullWidth
                  value={existing?.answer || ''}
                  onChange={(e) =>
                    onAnswerChange(sessionId, question.id, null, e.target.value)
                  }
                />
              </div>
            ) : null}
          </div>
        );
      })}
      <div style={{ textAlign: 'right' }}>
        <Button
          onClick={() => onSave(sessionId)}
          size="small"
          variant="contained"
          style={{ marginTop: '20px' }}
        >
          Submit {category.title} Answer
        </Button>
      </div>
      {CustomComponent && <CustomComponent {...customComponentProps} />}
      <Divider />
    </div>
  );
};

export default DynamicCategorySection; 